import { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { GREEN, CHARCOAL, BORDER, MUTED, MUTED_FAINT, labelMono, bodyFont } from '../../theme/tokens2';

// ─── Privacy sections data ────────────────────────────────────────────────────
const PRIVACY_SECTIONS = [
  {
    title: 'Gathering of Personal Data',
    paragraphs: [
      'You may interact with our website in ways that requires us to gather personal data (e.g. requesting to be matched with renovation firms, contacting us etc.). In each case, we only collect and use personal data we deem necessary or appropriate to fulfil your request. We will not disclose or misuse your data without consent. When requesting to be matched with renovation firms, we will only disclose your personal data to the renovation firm upon receiving your consent.',
    ],
  },
  {
    title: 'Use of Cookies',
    paragraphs: [
      'We use cookies and similar tracking technologies on our website and when providing our services. We track your behaviours on our site (e.g. clicks, duration of use,  general page behaviours etc.) to improve your user experience and learn how users interact with our website. The data we collect are aggregated and are NOT directly traceable to you, any single individual or group.',
    ],
  },
  {
    title: 'Recommendations Or Information Provided By The Platform',
    paragraphs: [
      'It is important for you and also your own due diligence to assess if any recommendations or information are applicable. If you decide to take up any recommendations or information provided, the Savvy Renovator assumes no liability for any damages (whether direct or speculative) of any kind that may arise out of it.',
    ],
  },
  {
    title: 'Third-Party Links',
    paragraphs: [
      'We are not responsible for the privacy practices of any third-party links on our website unless otherwise stated.',
    ],
  },
];

// ─── Hoverable link helper ────────────────────────────────────────────────────
function NavLink({ to, children, sx = {} }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      component={Link}
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        textDecoration: 'none',
        color: hovered ? CHARCOAL : MUTED,
        transition: 'color 0.2s ease',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Privacy page ─────────────────────────────────────────────────────────────
export default function Privacy() {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', ...bodyFont }}>

      {/* ── Top bar ── */}
      <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{
          maxWidth: 1400, mx: 'auto',
          px: { xs: 3, md: 6 },
          height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {/* Logo */}
          <Box component={Link} to="/" sx={{textDecoration: 'none'}}>
            <Typography sx={{ ...labelMono, color: CHARCOAL }}>Savvy Renovator</Typography>
          </Box>

          {/* Back link */}
          <NavLink to="/">
            <Typography sx={{ ...labelMono, fontSize: '0.75rem' }}>← Back to Home</Typography>
          </NavLink>
        </Box>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 3, md: 6 }, py: { xs: 10, md: 14 } }}>

        {/* Section label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
          <Typography sx={{ ...labelMono, color: GREEN }}>Disclaimers</Typography>
          <Box sx={{ height: '1px', width: 40, backgroundColor: `${GREEN}4d` }} />
        </Box>

        {/* Page title */}
        <Typography
          component="h1"
          sx={{
            fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
            fontWeight: 300, lineHeight: 1.06, letterSpacing: '-0.03em',
            color: CHARCOAL, mb: 2, ...bodyFont,
          }}
        >
          Privacy
        </Typography>

        {/* Date Last Updated */}
        <Typography sx={{ ...labelMono, color: MUTED_FAINT, mb: 8 }}>
          Last updated: May 2026
        </Typography>

        {/* Body sections */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

          {/* Rest of the Body Section */}
          {PRIVACY_SECTIONS.map((section) => (
            <Box key={section.title}>

              <Typography component="h2" 
              sx={{ fontSize: '1.125rem', fontWeight: 500, color: CHARCOAL, mb: 2, ...bodyFont }}>
                {section.title}
              </Typography>

              {section.paragraphs.map((paragraph, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: '1rem', color: MUTED, lineHeight: 1.8, ...bodyFont, }}
                >
                  {paragraph}
                </Typography>
              ))}

            </Box>
          ))}

        </Box>
      </Box>

      {/* ── Footer strip ── */}
      <Box sx={{ borderTop: `1px solid ${BORDER}` }}>
        <Box sx={{
          maxWidth: 1400, mx: 'auto',
          px: { xs: 3, md: 6 }, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Typography sx={{ ...labelMono, color: MUTED_FAINT }}>
            © {new Date().getFullYear()} Savvy Renovator
          </Typography>
          <NavLink to="/">
            <Typography sx={{ ...labelMono }}>Back to Home</Typography>
          </NavLink>
        </Box>
      </Box>
    </Box>
  );
}